import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import Home from "./components/Pages/Home";
import TeamReport from "./components/Pages/TeamReport/TeamReport";
import Explorer from "./components/Pages/Explorer/Explorer";
import GameLog from "./components/Pages/GameLog";
import Glossary from "./components/Pages/Glossary/Glossary";
import NavBar from "./components/Elements/NavBar/NavBar";
import GameReport from "./components/Pages/GameReport/GameReport";
import { Amplify } from "aws-amplify";
import { LicenseInfo } from "@mui/x-license-pro";
import {
  withAuthenticator,
  WithAuthenticatorProps,
} from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import IdentityReport from "./components/Pages/IdentityReport/IdentityReport";
import Rotations from "./components/Pages/Coaching/Rotations";
import { ExplorerEnvironment } from "./data/Cache";
import { isCoach } from "./data/ACL";
import TeamReportv2 from "./components/Pages/TeamReportv2/TeamReportv2";
import { LeagueEnvironmentProvider } from "./data/LeagueEnvironmentProvider";
import HalftimeDashboard from "./components/Pages/Coaching/HalftimeDashboard";
import Research from "./components/Pages/Research/Research";
import PlayerStats from "./components/Pages/PlayerStats/PlayerStats";
import {
  canUploadRebounding,
  canViewRebounding,
  Rebounding,
  ReboundingUpload,
} from "./components/Pages/Rebounding";
import Kpi from "./components/Pages/Kpi"; // KD1 KPI pages + dashboards

const MUI_PRO_KEY = process.env.REACT_APP_MUI_PRO_KEY;

Amplify.configure({
  Auth: {
    // OPTIONAL - Amazon Cognito User Pool ID
    userPoolId: "us-west-2_Wgn9i4TEw",

    // OPTIONAL - Amazon Cognito Web Client ID (26-char alphanumeric string)
    userPoolWebClientId: "snvmjfkd4300b55qmou7jgaei",

    // OPTIONAL - Enforce user authentication prior to accessing AWS resources or not
    mandatorySignIn: true,

    // OPTIONAL - This is used when autoSignIn is enabled for Auth.signUp
    // 'code' is used for Auth.confirmSignUp, 'link' is used for email link verification
    signUpVerificationMethod: "code", // 'code' | 'link'
  },
});

MUI_PRO_KEY && LicenseInfo.setLicenseKey(MUI_PRO_KEY);

function App({ user, signOut }: WithAuthenticatorProps) {
  if (!user || !user.attributes) return null;
  // Teams will load from cache immediately, then refresh in background
  // App can render while teams are being fetched/refreshed

  const { given_name, family_name } = user.attributes;

  return (
    <div className="App">
      <NavBar
        given_name={given_name}
        family_name={family_name}
        handleSignOut={signOut}
        user={user}
      />
      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/reports/nba" replace />} />
          <Route
            path="/reports"
            element={<Navigate to="/reports/nba" replace />}
          />
          <Route
            path="/legacy-reports/:teamAbbrev/:reportTypeString"
            element={<TeamReport />}
          />
          <Route
            path="/reports/nba/:teamAbbrev"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <TeamReportv2 />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/reports/gleague/:teamAbbrev"
            element={
              <LeagueEnvironmentProvider
                environment={ExplorerEnvironment.DRAFT}
              >
                <TeamReportv2 />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/reports/:teamAbbrev"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <TeamReportv2 />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/reports/nba"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <Home />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/reports/gleague"
            element={
              <LeagueEnvironmentProvider
                environment={ExplorerEnvironment.DRAFT}
              >
                <Home />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/explorer/nba"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <Explorer />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/explorer/draft"
            element={
              <LeagueEnvironmentProvider
                environment={ExplorerEnvironment.DRAFT}
              >
                <Explorer />
              </LeagueEnvironmentProvider>
            }
          />
          {/*
           * Player Stats — the roll-up blobs as a sortable league-wide table.
           * `/*` carries the per-player sub-route. Which garden gets read
           * follows the environment provider, so the two paths differ only in
           * that wrapper.
           */}
          <Route
            path="/stats/nba/*"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <PlayerStats />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/stats/draft/*"
            element={
              <LeagueEnvironmentProvider
                environment={ExplorerEnvironment.DRAFT}
              >
                <PlayerStats />
              </LeagueEnvironmentProvider>
            }
          />
          {/*
           * Research wiki. League-agnostic conceptually, but wrapping in
           * NBA env provider gives us a working ExplorerAPI instance for
           * embedded-query rendering (Phase 3) — all embedded saved
           * queries are NBA queries. `/*` catches sub-routes (article
           * detail, editor) once Phase 1+ add them.
           */}
          <Route
            path="/research/*"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <Research user={user} />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/glossary/nba"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <Glossary />
              </LeagueEnvironmentProvider>
            }
          />
          <Route
            path="/glossary/draft"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.DRAFT}>
                <Glossary />
              </LeagueEnvironmentProvider>
            }
          />
          <Route path="/games" element={<GameLog />} />
          <Route path="/games/:gameId" element={<GameReport />} />
          <Route path="/id-report" element={<IdentityReport />} />
          <Route
            path="/coaching/halftime-dashboard/:gameId"
            element={<HalftimeDashboard />}
          />
          {isCoach(user) && (
            <Route path="/coaching/rotations" element={<Rotations />} />
          )}
          {/* KPI dashboards + postgame packet pages (KD1). */}
          <Route
            path="/kpi/nba/*"
            element={
              <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                <Kpi />
              </LeagueEnvironmentProvider>
            }
          />
          {/* Rebounding board (RD1): Coaches view; DevTeam / ReboundingDashboardUsers also upload. The API checks the same groups. */}
          {canViewRebounding(user) && (
            <Route
              path="/rebounding"
              element={
                <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                  <Rebounding canUpload={canUploadRebounding(user)} />
                </LeagueEnvironmentProvider>
              }
            />
          )}
          {canUploadRebounding(user) && (
            <Route
              path="/rebounding/upload"
              element={
                <LeagueEnvironmentProvider environment={ExplorerEnvironment.NBA}>
                  <ReboundingUpload />
                </LeagueEnvironmentProvider>
              }
            />
          )}
        </Routes>
      </main>
    </div>
  );
}

export default withAuthenticator(App, { hideSignUp: true });
